// @core/stores/secret/SecretCertificateStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { secretApi } from '@core/api';
import type {
  Certificate,
  CertificateResponse,
  IssueCertificateRequest,
  ListCertificatesResponse,
  RevokeCertificateRequest,
} from '@core/types/secret';
import type { PageInfo } from '@core/types/pagination';

class SecretCertificateStore {
  // List state
  items: Certificate[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Certificate | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Certificate>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Certificate | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Certificate | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Get certificate
   */
  async GetCertificate(engine_id: string, serial: string): Promise<Certificate> {
    // Check cache first
    const cached = this.cache.get(serial);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await secretApi.GetCertificate(engine_id, serial);
      const item = response.certificate;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Issue certificate
   */
  async IssueCertificate(engine_id: string, role_name: string, data: IssueCertificateRequest): Promise<CertificateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.IssueCertificate(engine_id, role_name, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List certificates
   */
  async ListCertificates(engine_id: string, params?: { cursor?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; sort?: string | number | boolean; status?: string | number | boolean; type?: string | number | boolean; }): Promise<ListCertificatesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.ListCertificates(engine_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke certificate
   */
  async RevokeCertificate(engine_id: string, data: RevokeCertificateRequest): Promise<CertificateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.RevokeCertificate(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const secretCertificateStore = new SecretCertificateStore();
